/**
 * The router, built from the modules.
 *
 * This file names no screen. It walks the registry, keeps the routes this person
 * is allowed to open, and sends them to the first thing they CAN use - which for
 * a cashier is the till and for the owner is the dashboard, without either being
 * written down here.
 */
import { useEffect, useMemo } from 'react';
import { Navigate, Route, Routes, matchPath, useLocation } from 'react-router-dom';
import { FEATURES, featureOf } from '@pos/shared';
import { useI18n, useT } from './core/i18n/index.js';
import { useAuth } from './core/state/auth.js';
import { useSettings } from './core/state/settings.js';
import { AppShell } from './core/ui/AppShell.js';
import { Empty, Loading } from './core/ui/ui.js';
import { allNav, allRoutes, featureOn, itemAllowed } from './core/module.js';
import { Login } from './modules/auth/Login.js';
import './modules/index.js';

export function App() {
  const t = useT();
  const { lang } = useI18n();
  const { user, ready, can, hasFeature } = useAuth();
  const { settings } = useSettings();
  const { pathname } = useLocation();

  const routes = useMemo(() => allRoutes(), []);
  const allowed = useMemo(
    () => routes.filter((r) => itemAllowed(r, can) && featureOn(r, settings)),
    [routes, can, settings],
  );

  /**
   * Where to send someone who has nowhere better to be - which includes the
   * front door. A shop opens this to serve a customer, so the till is what it
   * gets; the owner's figures are a screen you go to, not one you land on.
   */
  // The browser tab carries the shop's own name once someone has signed in;
  // before that the page is AppBay's.
  useEffect(() => {
    const name = user ? settings.storeNameEn || settings.storeNameAr : '';
    document.title = name ? `${name} · POS` : t('app.brand');
  }, [user, settings.storeNameEn, settings.storeNameAr, t]);

  const home = useMemo(() => {
    const nav = allNav().filter((i) => itemAllowed(i, can) && featureOn(i, settings));
    const till = nav.find((i) => i.to === '/checkout');
    return till?.to ?? nav[0]?.to ?? '/checkout';
  }, [can, settings]);

  if (!ready) return <Loading />;
  if (!user) return <Login />;

  // A route can carry a parameter (/bills/:id), so match the way the router
  // does instead of comparing strings - otherwise every detail page loses its
  // heading.
  const current = routes.find((r) => matchPath(r.path, pathname));
  const title = current?.titleKey ? t(current.titleKey) : '';

  return (
    <AppShell title={title}>
      <Routes>
        {allowed.map((r) => (
          <Route key={r.path} path={r.path} element={r.element} />
        ))}

        {/* A screen that exists but is not theirs: say so plainly, do not 404. */}
        {routes
          .filter((r) => !allowed.includes(r))
          .map((r) => (
            <Route
              key={`denied-${r.path}`}
              path={r.path}
              element={
                (() => {
                  // A module this shop does not have: nobody in the shop can
                  // grant it, so do not send them looking at roles.
                  const f = r.permission ? featureOf(r.permission) : null;
                  const meta = f && !hasFeature(f) ? FEATURES.find((x) => x.id === f) : null;
                  return meta ? (
                    <Empty
                      icon="🧩"
                      title={t('error.moduleOff', { name: lang === 'ar' ? meta.ar : meta.en })}
                      hint={t('error.moduleOffHint')}
                    />
                  ) : null;
                })() ??
                (!featureOn(r, settings) ? (
                  /* Not a refusal: the shop has this part switched off. */
                  <Empty icon="🔌" title={t('error.switchedOff')} hint={t('error.switchedOffHint')} />
                ) : (
                  <Empty icon="🔒" title={t('error.notAllowed')} hint={t('roles.askOwner')} />
                ))
              }
            />
          ))}

        <Route path="*" element={<Navigate to={home} replace />} />
      </Routes>
    </AppShell>
  );
}
